import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import { Box, Group, Divider, UnstyledButton } from '@mantine/core';
import Button from '../../../new_arch/components/Button/Button';
import Text from '../../../new_arch/modules/ui/components/Text/Text';
import Callout from '../../../new_arch/modules/ui/components/Callout/Callout';
import PlanSettingsPresenter from '../../../presenters/PlanSettings/PlanSettingsPresenter';
import {
	SUCCESS_700, SUCCESS_100, PRIMARY_BASE,
	SECONDARY_BASE, SECONDARY_PRESSED, NEUTRAL_800, NEUTRAL_600, WHITE, BLACK
} from '../../../styles/colors';

const TOTAL_AMOUNT_STYLE = { fontSize: '32px', fontWeight: 700, letterSpacing: '-0.38px', color: NEUTRAL_800, lineHeight: 1 };
const RENEWS_TEXT_STYLE = { lineHeight: '22px' };

const FreeTrialHeader = observer( ( { presenter } ) => (
	<Group position="apart" align="center">
		<Group spacing={4} align="baseline">
			<Text style={{ fontSize: '28px', fontWeight: 700, lineHeight: '28px', letterSpacing: '-0.53px', color: BLACK, marginRight: '4px' }}>{presenter.daysLeftOnFreeTrial}</Text>
			<Text variant="body" color={BLACK}>days left until free trial ends</Text>
		</Group>
		<Button
			style={{ minWidth: '140px' }}
			disabled={presenter.upgradeButtonData.disabled}
			size="md"
			type="primary"
			onClick={presenter.onUpgradePlanButtonClicked}
		>
			{presenter.upgradeButtonData.text}
		</Button>
	</Group>
) );

FreeTrialHeader.propTypes = {
	presenter: PropTypes.instanceOf( PlanSettingsPresenter ).isRequired
};

const PlanRow = ( { label, price } ) => (
	<tr>
		<td style={{ padding: '6px 0', color: SECONDARY_BASE }}><Text variant="body-bold">{label}</Text></td>
		<td style={{ padding: '6px 0', textAlign: 'right', verticalAlign: 'middle', color: NEUTRAL_800 }}><Text variant="body-bold">{price}</Text></td>
	</tr>
);

PlanRow.propTypes = {
	label: PropTypes.string.isRequired,
	price: PropTypes.string.isRequired
};

export const PlanDetails = observer( ( { presenter, showPerMonth = true } ) => (
	<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '20px 24px', borderRadius: '8px', backgroundColor: WHITE }}>
		<Text variant="h2" color={NEUTRAL_800}>{presenter.planName}</Text>
		<table style={{ width: '100%', borderCollapse: 'collapse' }}>
			<tbody>
				<PlanRow label={presenter.planRowLabel} price={presenter.planCostForDisplay} />
				<PlanRow label={presenter.membersAddOnRowLabel} price={presenter.membersAddOnCostForDisplay} />
				<PlanRow label={presenter.storageAddOnRowLabel} price={presenter.storageAddOnCostForDisplay} />
			</tbody>
		</table>
		<Divider color={NEUTRAL_600} />
		<Group position="apart" align="flex-start">
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
				<Text variant="h2" color={NEUTRAL_800}>Total</Text>
				{presenter.billingNote && (
					<Text variant="caption-regular" color={SECONDARY_BASE}>
						*{presenter.billingNote}
					</Text>
				)}
			</Box>
			<Box sx={{ textAlign: 'right' }}>
				<Text style={TOTAL_AMOUNT_STYLE}>{presenter.totalCostForDisplay}</Text>
				{showPerMonth && <Text variant="h5" color={NEUTRAL_800}>per month*</Text>}
			</Box>
		</Group>
	</Box>
) );

PlanDetails.propTypes = {
	presenter: PropTypes.instanceOf( PlanSettingsPresenter ).isRequired,
	showPerMonth: PropTypes.bool
};

const SubscriptionFooter = observer( ( { presenter } ) => (
	<Group position="apart" align="flex-start">
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
			<Group spacing={4}>
				<Text variant="body-bold" color={SECONDARY_PRESSED} style={RENEWS_TEXT_STYLE}>Renews:</Text>
				<Text variant="body-bold" color={SECONDARY_PRESSED} style={RENEWS_TEXT_STYLE}>{presenter.formatedBillingPeriodEnd}</Text>
			</Group>
			<UnstyledButton onClick={presenter.onGoToBillingClicked}>
				<Text variant="h5" color={PRIMARY_BASE}>Go to Billing</Text>
			</UnstyledButton>
		</Box>
		<Button
			style={{ minWidth: '140px' }}
			disabled={presenter.upgradeButtonData.disabled}
			size="md"
			type="secondary"
			onClick={presenter.onUpgradePlanButtonClicked}
		>
			{presenter.upgradeButtonData.text}
		</Button>
	</Group>
) );

SubscriptionFooter.propTypes = {
	presenter: PropTypes.instanceOf( PlanSettingsPresenter ).isRequired
};

const ActiveSubscriptionDetail = ( { presenter } ) => (
	<Box
		sx={{
			display: 'flex',
			flexDirection: 'column',
			gap: '24px',
			padding: '24px',
			borderRadius: '24px',
			border: `2px solid ${SUCCESS_700}`,
			backgroundColor: SUCCESS_100,
			width: '100%',
			maxWidth: '744px',
			boxSizing: 'border-box'
		}}
	>
		{presenter.isOnActiveFreeTrial
			? <FreeTrialHeader presenter={presenter} />
			: null}
		<PlanDetails presenter={presenter} />
		{presenter.isOnActiveFreeTrial && presenter.trialSeatsAdded > 0 && (
			<Callout
				type="promotional"
				variant="outlined"
				text={`You added ${presenter.trialSeatsAdded} ${presenter.trialSeatsAdded === 1 ? 'seat' : 'seats'} during your trial. You can adjust seats below.`}
				styles={{ text: { lineHeight: '22px' } }}
			/>
		)}
		{presenter.workspace.subscription?.inStudioTrial && (
			<Group position="right">
				<Button
					style={{ minWidth: '140px' }}
					disabled={presenter.upgradeButtonData.disabled}
					size="md"
					type="secondary"
					onClick={presenter.onChangePlanButtonClicked}
				>
					Change Plan
				</Button>
			</Group>
		)}
		{!presenter.isOnActiveFreeTrial && <SubscriptionFooter presenter={presenter} />}
	</Box>
);

ActiveSubscriptionDetail.propTypes = {
	presenter: PropTypes.instanceOf( PlanSettingsPresenter ).isRequired
};

export default observer( ActiveSubscriptionDetail );
